/* eslint-disable @typescript-eslint/no-explicit-any */
import { ProjectRoleId } from "@clovis/server/src/app/domain/project/role/types";
import type {
  AnyVariables,
  Operation,
  OperationResult,
  SubscriptionHandler,
  UseMutationResponse,
  UseQueryResponse,
  UseSubscriptionArgs,
  UseSubscriptionResponse,
} from "urql";
import { v4 as uuidv4 } from "uuid";
import type { Cache } from "~/cache/graphcache/src";
import type {
  CreateTaskInput,
  GetProjectLabelsQuery,
  GetTeamsQuery,
  GetUsersQuery,
  Tasks_Bool_Exp,
  Tasks_Order_By,
  UserFieldsFragment,
} from "~/gql/graphql";
import {
  GetProjectLabelsDocument,
  GetTeamsDocument,
  GetUsersDocument,
  ProjectTasksList_DataDocument,
} from "~/gql/graphql";
import type { LabelsMultiSelect_ProjectFragment } from "~/screens/App/components/LabelsMultiSelectFieldInput/LabelsMultiSelectFieldInput.graphql";
import { getOrgsOptions } from "~/screens/App/screens/Project/Tasks/components/CreateTaskForm/components/AssigneesMultiSelectFieldInput/AssigneesMultiSelectFieldInput";

type QueryHook = () => UseQueryResponse<any, any>;

type MutationHook = () => UseMutationResponse<any, any>;

type SubscriptionHook = <TData = any>(
  options: Omit<UseSubscriptionArgs<any>, "query">,
  handler?: SubscriptionHandler<any, TData>
) => UseSubscriptionResponse<any, any>;

type GetSubscriptionData<T extends SubscriptionHook> = Parameters<
  NonNullable<Parameters<T>["1"]>
>["1"];

type Hook = MutationHook | QueryHook | SubscriptionHook;

type GetDataFromHook<T extends Hook> = T extends SubscriptionHook
  ? GetSubscriptionData<T>
  : ReturnType<T> extends UseQueryResponse<infer X, any>
  ? X
  : ReturnType<T> extends UseMutationResponse<infer X, any>
  ? X
  : never;

type GetVariablesFromHook<T extends Hook> =
  ReturnType<T> extends UseQueryResponse<any, infer X>
    ? X
    : ReturnType<T> extends UseMutationResponse<any, infer X>
    ? X
    : ReturnType<T> extends UseSubscriptionResponse<any, infer X>
    ? X
    : never;

type GetOperation<T extends Hook> = Operation<
  GetDataFromHook<T>,
  GetVariablesFromHook<T>
>;

type UrqlParameterResponse<
  Data = any,
  Variables extends AnyVariables = AnyVariables
> = Pick<OperationResult<Data, Variables>, "data" | "error">;

type UrqlResponse<T extends Operation> = UrqlParameterResponse<
  GetDataFromOperation<T>,
  GetVariablesFromOperation<T>
>;

type GetDataFromOperation<T extends Operation> = T extends Operation<infer X>
  ? X
  : never;

type GetVariablesFromOperation<T extends Operation> = T extends Operation<
  unknown,
  infer X
>
  ? X
  : never;

type UrqlParameter<T extends Operation> = (
  o: T
) => Promise<UrqlResponse<T>> | UrqlResponse<T>;

type StoredVariables = {
  filters: Tasks_Bool_Exp;
  limit: number;
  projectId: string;
  sort: Tasks_Order_By[];
};

type ProjectData = {
  labels: GetProjectLabelsQuery["project_labels"];
  users: GetUsersQuery["project"];
  teams: GetTeamsQuery["project"];
};

const generateId = () => {
  return uuidv4();
};

// Store the last generated temporary task ID for offline consistency
let lastGeneratedTaskId: string | null = null;

/**
 * Generates a temporary task ID for optimistic updates.
 * Used for creating tasks while offline to maintain consistency between
 * optimistic updates and actual mutation updates.
 *
 * @param returnLastGenerated If true, returns the last generated ID for consistency
 *                           across the offline task creation lifecycle (before mutation,
 *                           optimistic update, and mutation updater)
 * @returns A temporary task id with format "temp_{uuid}"
 */
export const generateTemporaryTaskId = (
  returnLastGenerated = false
): string => {
  if (returnLastGenerated && lastGeneratedTaskId) {
    return lastGeneratedTaskId;
  }

  const newTaskId = `temp_${generateId()}`;
  lastGeneratedTaskId = newTaskId;
  return newTaskId;
};

export const getStoredVariables = (): StoredVariables => {
  return JSON.parse(
    localStorage.getItem("variables") ?? "{}"
  ) as StoredVariables;
};

export const readProjectDataFromCache = (
  cache: Cache,
  projectId: string
): ProjectData => {
  const projectLabelsQuery = cache.readQuery({
    query: GetProjectLabelsDocument,
    variables: { projectId },
  });

  const projectUsersQuery = cache.readQuery({
    query: GetUsersDocument,
    variables: { id: projectId },
  });

  const projectTeamsQuery = cache.readQuery({
    query: GetTeamsDocument,
    variables: { id: projectId },
  });

  return {
    labels: projectLabelsQuery?.project_labels ?? [],
    teams: projectTeamsQuery?.project ?? null,
    users: projectUsersQuery?.project ?? null,
  };
};

export const getActiveProjectUsers = (projectData: ProjectData) => {
  return (
    projectData.users?.members
      ?.filter((member) => member.role.name !== ProjectRoleId.Disabled)
      .map((member) => member.user) ?? []
  );
};

export const createTaskAssignations = (
  input: CreateTaskInput,
  projectData: ProjectData,
  orgOptions: ReturnType<typeof getOrgsOptions>
) => {
  const currentTime = new Date().toISOString();
  const emptyCreator = {
    __typename: "users" as const,
    full_name: "",
    id: "",
    user_avatar: {
      __typename: "user_avatars" as const,
      public_url: "",
    },
  };

  return {
    __typename: "task_assignations" as const,
    orgs:
      input.assignations?.orgs.map((orgId) => {
        const org = orgOptions.find((org) => org.value.id === orgId);
        return {
          __typename: "task_assignations_orgs" as const,
          created_at: currentTime,
          creator: emptyCreator,
          org: {
            __typename: "orgs" as const,
            name: org?.label,
            org_avatar: {
              __typename: "org_avatars" as const,
              public_url: org?.avatar,
            },
          },
          org_id: org?.value.id,
        };
      }) ?? [],
    teams:
      input.assignations?.teams.map((teamId) => {
        const team = projectData.teams?.teams.find(
          (team) => team.id === teamId
        );
        return {
          __typename: "task_assignations_teams" as const,
          created_at: currentTime,
          creator: emptyCreator,
          team: {
            __typename: "teams" as const,
            color: team?.color,
            name: team?.name,
          },
          team_id: team?.id,
        };
      }) ?? [],
    users:
      input.assignations?.users?.map((userId) => {
        const member = projectData.users?.members.find(
          (member) => (member?.user as { id: string })?.id === userId
        );
        const user = member?.user as {
          id: string;
          full_name: string;
          user_avatar: { public_url: string };
        };

        return {
          __typename: "task_assignations_users" as const,
          created_at: currentTime,
          creator: emptyCreator,
          user: {
            __typename: "users" as const,
            full_name: user?.full_name,
            user_avatar: {
              __typename: "user_avatars" as const,
              public_url: user?.user_avatar?.public_url,
            },
          },
          user_id: userId,
        };
      }) ?? [],
  };
};

export const createTaskLabels = (
  input: CreateTaskInput,
  projectData: ProjectData
) => {
  return (
    input.label_ids?.map((labelId) => {
      const label = projectData.labels.find(
        (label) => (label as LabelsMultiSelect_ProjectFragment).id === labelId
      );
      return {
        __typename: "tasks_to_project_labels" as const,
        label,
      };
    }) ?? []
  );
};

export const createOptimisticTask = (
  input: CreateTaskInput,
  taskId: string,
  lastTaskNumber: number,
  projectData: ProjectData,
  orgOptions: ReturnType<typeof getOrgsOptions>
) => {
  return {
    __typename: "tasks" as const,
    ancestors: [],
    assignations: createTaskAssignations(input, projectData, orgOptions),
    attachments: [],
    created_at: new Date().toISOString(),
    creator: {
      __typename: "users" as const,
      full_name: "Current User",
      id: "temp_creator_id",
    },
    deleted: false,
    deleted_at: null,
    description: input.description,
    end_date: input.end_date ?? null,
    hours_done: 0,
    hours_expected: input.hours_expected ?? 0,
    id: taskId,
    is_validated_by: "none" as const,
    labels: createTaskLabels(input, projectData),
    location: input.location ?? null,
    number: lastTaskNumber,
    permissions: null,
    predecessors: [],
    project: {
      __typename: "projects" as const,
      id: input.project_id,
      name: "Loading...",
      project_avatar: null,
    },
    project_id: input.project_id,
    start_date: input.start_date ?? null,
    subtasks:
      input.subtasks?.map((subtask) => ({
        __typename: "subtasks" as const,
        closed: subtask.closed,
        description: subtask.description,
        end_date: subtask.end_date ?? null,
        id: generateId(),
        start_date: subtask.start_date ?? null,
      })) ?? [],
    subtasks_progress: null,
  };
};

export const updateTasksListCache = (
  cache: Cache,
  variables: StoredVariables,
  newTask: ReturnType<typeof createOptimisticTask>
) => {
  cache.updateQuery(
    {
      query: ProjectTasksList_DataDocument,
      variables,
    },
    (data) => {
      if (!data || !data.projects_by_pk) {
        return data;
      }

      return {
        ...data,
        projects_by_pk: {
          ...data.projects_by_pk,
          tasks: [newTask, ...(data.projects_by_pk.tasks ?? [])],
          tasks_count: {
            ...data.projects_by_pk.tasks_count,
            aggregate: {
              ...data.projects_by_pk.tasks_count?.aggregate,
              count:
                (data.projects_by_pk.tasks_count?.aggregate?.count ?? 0) + 1,
            },
          },
          tasks_count_filtered: {
            ...data.projects_by_pk.tasks_count_filtered,
            aggregate: {
              ...data.projects_by_pk.tasks_count_filtered?.aggregate,
              count:
                (data.projects_by_pk.tasks_count_filtered?.aggregate?.count ??
                  0) + 1,
            },
          },
        },
      };
    }
  );
};

export const handleCreateTaskOptimisticUpdate = (
  input: CreateTaskInput,
  cache: Cache
): void => {
  const taskId = generateTemporaryTaskId(true);
  const storedVariables = getStoredVariables();
  const projectData = readProjectDataFromCache(
    cache,
    storedVariables.projectId
  );
  const activeUsers = getActiveProjectUsers(projectData);
  const orgOptions = getOrgsOptions(activeUsers as UserFieldsFragment[]);

  // Get the last task number
  const currentData = cache.readQuery({
    query: ProjectTasksList_DataDocument,
    variables: storedVariables,
  });

  const lastCreatedTask = currentData?.projects_by_pk?.tasks[0] as {
    number?: number;
  };
  const lastTaskNumber = (lastCreatedTask?.number ?? 0) + 1;

  const newTask = createOptimisticTask(
    input,
    taskId,
    lastTaskNumber,
    projectData,
    orgOptions
  );

  updateTasksListCache(cache, storedVariables, newTask);
};

export type {
  GetOperation,
  ProjectData,
  StoredVariables,
  UrqlParameter,
  UrqlResponse,
};
